import { memo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import { verifyCode } from '@/data/services/activityElement/codeEditorActivityProgressServices';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '@/components/icons';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { LanguageCodeEditorEnum } from '@/models/CodeEditorActivity';
import { CodeValidationResult } from './CodeValidationResult';

type Props = {
  activityId: number;
  progressId: number;
  disabled?: boolean;
  sourceCode: string;
  language: LanguageCodeEditorEnum;
  onValidateRequest?: () => void;
};

const CodeValidation = (props: Props) => {
  const [t] = useTranslation('translation', {
    keyPrefix: 'codeValidation',
  });

  const {
    activityId,
    progressId,
    disabled,
    sourceCode,
    language,
    onValidateRequest,
  } = props;

  const {
    data: results,
    isLoading: loading,
    error,
    mutate: verify,
  } = useMutation(verifyCode);

  const handleVerifyCode = () => {
    if (onValidateRequest) {
      onValidateRequest();
    }
    verify({ activityId, progressId });
  };

  return (
    <section className="my-5 flex flex-col gap-2">
      <MainButton
        disabled={disabled}
        text={`${t('runTests')} >>`}
        className="capitalize"
        loading={loading}
        onClick={handleVerifyCode}
      />

      <Text text={t('beforeDeliveringActivity')} className="text-neutral/50" />

      <AnimatePresence>
        <ConditionalRenderer
          condition={loading}
          fallback={
            <motion.section
              key="validation-result"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="w-full"
            >
              {results ? (
                <CodeValidationResult
                  results={results}
                  sourceCode={sourceCode}
                  language={language}
                />
              ) : (
                <Text className="text-red-500" text={getErrorMessage(error)} />
              )}
            </motion.section>
          }
        >
          <motion.section
            key="loading"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="w-full flex items-center justify-center"
          >
            <LoadingIcon
              className="w-10 h-10 text-primary"
              data-testid="loading-icon"
            />
          </motion.section>
        </ConditionalRenderer>
      </AnimatePresence>
    </section>
  );
};

export default memo(CodeValidation);
